/**
 * Componente: Galería de diseños del municipio (hasta el límite de cada municipio)
 * Pantalla de entrada del Diseñador cuando ya hay al menos un diseño.
 */

import React, { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import LicenseCard from './LicenseCard';
import { IconAgregar, IconAdvertencia } from './icons';
import { useClasesMunicipio, clasesDeEjemplo } from '../utils/clasesLicencia';
import { clasesSeCortanEnDiseno, useTipografiasListas } from '../utils/formatoClases';
import '../styles/padron.css';
import '../styles/disenador.css';

const CONTRIBUYENTE_EJEMPLO = {
  apellido: 'PÉREZ',
  nombre: 'JUAN CARLOS',
  tipo_documento: 'DNI',
  numero_documento: '30123456',
  fecha_nacimiento: '1985-06-15',
  fecha_emision: '2026-01-15',
  fecha_vencimiento: '2031-01-15',
  clases: ['B1', 'B2'],
  grupo_sanguineo: 'O+',
  donante_organos: true,
  foto_url: null,
};

// Invitados demo de tumunicipio (panel de Superadmin → Demos): el login deja
// `demo: true` en el usuario de la sesión. Es solo para NO mostrarles un botón que
// el backend les va a rechazar (DELETE /disenos/:id → 403 DEMO_SIN_PERMISO): el
// permiso real lo pone el backend con el claim `demo` del token, no esto.
const esInvitadoDemo = () => {
  try {
    return !!JSON.parse(sessionStorage.getItem('usuario'))?.demo;
  } catch (e) {
    return false;
  }
};

// `limite` = el tope de diseños de este municipio, tal como lo devuelve el
// backend (GET /api/municipios/disenos → limite_disenos): puede ser el general
// o uno propio del cliente, por eso no está escrito acá. null = todavía no se
// sabe (no cargó, o respuesta sin el dato): no se muestra ni se bloquea nada
// por cantidad y el backend sigue haciendo cumplir el tope real.
const DisenadorGaleria = ({ disenos, limite, onNuevo, onEditar, onActivar, onDuplicar, onEliminar, procesandoId, onVolverDashboard }) => {
  // Contribuyente de ejemplo de las miniaturas: con las primeras clases del nomenclador del municipio.
  const { catalogo } = useClasesMunicipio();
  useTipografiasListas();
  const clasesEjemplo = useMemo(() => clasesDeEjemplo(catalogo), [catalogo]);
  const contribuyenteEjemplo = useMemo(() => ({ ...CONTRIBUYENTE_EJEMPLO, clases: clasesEjemplo }), [clasesEjemplo]);
  const alcanzoMaximo = limite != null && disenos.length >= limite;
  // Si Superadmin bajó el límite por debajo de lo que ya tenía, no se borra
  // nada: solo se bloquea crear y duplicar hasta que borre los que sobran.
  const sobran = limite != null ? Math.max(0, disenos.length - limite) : 0;
  const esElUnico = disenos.length <= 1;
  const demo = esInvitadoDemo();

  return (
    <section className="disenador-section">
      <div className="disenador-galeria-header">
        <div>
          {onVolverDashboard && (
            <button type="button" className="btn-volver disenador-galeria-btn-volver" onClick={onVolverDashboard}>
              <ArrowLeft size={16} /> Volver
            </button>
          )}
          <h2>Diseños de licencia</h2>
          <p className="form-hint">
            {limite != null ? `${disenos.length} de ${limite} diseños.` : `${disenos.length} ${disenos.length === 1 ? 'diseño' : 'diseños'}.`}{' '}
            El que está marcado "Activo" es el que se usa en la Vista Previa del Padrón.
          </p>
          {demo && (
            <p className="form-hint">
              Con tu cuenta de demostración podés crear, duplicar, activar y editar diseños, pero no borrarlos.
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn-nuevo-contribuyente"
          onClick={onNuevo}
          disabled={alcanzoMaximo}
          title={alcanzoMaximo ? `Ya tenés el máximo de ${limite} diseños` : ''}
        >
          <IconAgregar size={16} /> Nuevo diseño
        </button>
      </div>

      {alcanzoMaximo && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          {sobran > 0 ? (
            <p>
              Tenés {disenos.length} diseños y el máximo es {limite}: no se pueden crear ni duplicar diseños hasta que
              borres {sobran === 1 ? 'uno' : sobran}.
            </p>
          ) : (
            <p>Llegaste al máximo de {limite} diseños. Borrá uno para poder crear otro.</p>
          )}
        </div>
      )}

      <div className="disenador-galeria-grid">
        {disenos.map((diseno) => (
          <div key={diseno.id} className={`disenador-galeria-card ${diseno.activo ? 'activo' : ''}`}>
            {diseno.activo && <span className="badge-activo">Activo</span>}

            <div className="disenador-galeria-miniatura-marco">
              <div className="disenador-galeria-miniatura">
                {/* LicenseCard resuelve solo: layout con elementos, solo imagen
                    de fondo, o aviso "sin diseño configurado" si la cara está
                    vacía. Ya no hay chequeo de "completo" (foto+DNI) — el
                    sistema no juzga si el diseño está terminado, lo hace el
                    usuario con la Vista Previa. */}
                <LicenseCard contribuyente={contribuyenteEjemplo} diseno={diseno} />
              </div>
            </div>

            <div className="disenador-galeria-card-info">
              <div className="disenador-galeria-nombre">{diseno.nombre}</div>
              {clasesSeCortanEnDiseno(diseno, clasesEjemplo, catalogo) && (
                <div className="canvas-aviso-clases canvas-aviso-clases--cortado" role="alert">
                  Las clases de este diseño no entran completas y se cortan con "…" (con las 3 descripciones más largas
                  del municipio). Revisalo en el editor.
                </div>
              )}

              <div className="disenador-galeria-acciones">
                {!diseno.activo && (
                  <button
                    type="button"
                    className="btn-accion btn-editar"
                    onClick={() => onActivar(diseno.id)}
                    disabled={procesandoId === diseno.id}
                  >
                    Usar como activo
                  </button>
                )}
                <button type="button" className="btn-accion btn-editar" onClick={() => onEditar(diseno)}>
                  Editar
                </button>
                <button
                  type="button"
                  className="btn-accion btn-editar"
                  onClick={() => onDuplicar(diseno.id)}
                  disabled={alcanzoMaximo || procesandoId === diseno.id}
                >
                  {procesandoId === diseno.id ? (
                    <>
                      <span className="btn-spinner"></span> Duplicando...
                    </>
                  ) : (
                    'Duplicar'
                  )}
                </button>
                {!demo && (
                  <button
                    type="button"
                    className="btn-accion btn-imprimir"
                    onClick={() => onEliminar(diseno)}
                    disabled={esElUnico || procesandoId === diseno.id}
                    title={esElUnico ? 'No se puede eliminar el único diseño' : ''}
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default DisenadorGaleria;
